/**
 * プロフィールのアクセント色。
 *
 * **無制限のカラーピッカーにはしない。** 暗い色を選ばれると地(#121212)に沈んで見えなくなる。
 * 自由指定そのものは Custom パレットで提供しているが、あれは
 * **選べる座標系を安全域に限定してある**ので下限を割る色は表現できない
 * （constants/accentCustom.ts）。ここを「自由入力を許した」と読み替えないこと。
 *
 * **下限は 4.5:1。** グラフィック要素の基準 3:1 より厳しいが、これは潔癖ではなく実装上の
 * 要件で、アクセントは ProfilePage で MUI の `palette.primary.main` に注入される。
 * MUI は primary.main をテキストにも使うので、テキストの基準を満たす必要がある。
 * ここを 3:1 に緩めれば紺色寄りの青などが解禁されるが、その前に primary.main を
 * 「アクセントを明度側に補正した別の色」に差し替える必要がある。
 * 例: 純青 #0000ff は 2.18:1、Tailwind blue-600 #2563eb でも 3.62:1。
 *
 * **例外が1件ある。** `neon-blue #0051ff` は 3.23:1 で下限を割っている（3:1 は満たす）。
 * 濃い青が欲しいという判断で意図的に通したもの。生成規則に任せると #3b74ff（4.57）になる。
 * 例外は生成スクリプトの EXCEPTIONS に理由付きで登録してあり、実行するたび一覧に出る。
 * **増やす前に上記の primary.main の差し替えを検討すること。**
 *
 * 保存するのは **key** であって色そのものではない。
 * こうしておくと、後から色を調整してもユーザーデータを触らずに済む。
 *
 * ---- 構造 ----
 * solid(12) と pastel(8) は **Tailwind CSS の値が土台。色そのものを自作しない。**
 *   solid = Tailwind 400 と 500 の中点（通称450）/ pastel = Tailwind 300
 * neon(8) だけは自前。既存パレットに「本物の蛍光色」は存在しないため。
 * 6色は色相から最大彩度を生成し、red / blue の2色だけ手で選んである。
 *
 * **450 は Tailwind に存在しない**（スケールは 50/100/…/400/500/…）ので、400 と 500 の
 * OKLab 中点として生成している。sRGB のまま平均すると中間が濁るので、必ず知覚均等な
 * 空間で補間すること。400 だと実機で彩度が物足りず、500 だと indigo が下限を割る:
 *   400  最小コントラスト 6.28 / 下限割れ 0件
 *   450  最小コントラスト 5.18 / 下限割れ 0件   ← 採用
 *   500  最小コントラスト 4.19 / 下限割れ 1件(indigo)
 *
 * **並び順はブランド色（ライム / 色相120°）から始めて色相順。** 各パレットの先頭が
 * 「そのパレットのライム」になり、緑→シアン→青→紫→ピンク→赤→橙 と回って戻る。
 * 赤から始める並びにしていたが、テーマ色から始まる方が PULSE の色として自然。
 *
 * Material ではなく Tailwind を土台にしたのは、暗い地での実測差。Material 500 は
 * 4件が 4.5:1 を割る（deeppurple 2.56 等）。Material 自身が暗い地では 200〜300 を
 * 使えという指針を出していて、実測もそれを裏付けている。
 *
 * **色数は絞ってある。** Tailwind は開発者がコード上で名前で選ぶためのファミリー定義で、
 * 利用者が丸を並べて見比べるためのものではない。全17色だと red と rose が ΔE 0.027 で
 * 区別できない。互いに最も離れる組み合わせを全探索で選んである（詳細は生成スクリプト）。
 *
 * **neon に入れてよい色相は限られている。** 最大彩度色が solid / pastel の色と近ければ
 * 別枠にする意味が無い。**判定は上の19色からの距離で行う**（使っていない色を参照にしない。
 * 一度 Material 500 等も含めて測って red と blue を誤って除外した）。
 * 0.08 以上を確保できるのは 0〜40° / 110〜124° / 138〜154° / 252〜258° / 318〜358°。
 * 入らないのはオレンジ〜黄(46〜106°)とスカイ(230〜246°)で、solid が既にその領域で
 * 十分鮮やかなため最大彩度にしても差が出ない。
 *
 * 【この表を手で編集しないこと】
 * scripts/genAccentPresets.js の出力を貼る。以前ここは手打ちされていて、14件中6件の
 * コントラストが実測とズレていた（indigo は 4.30 と書かれて実際は 3.89 で下限割れ）。
 * スクリプトはコントラスト下限と、同じパレット内の色同士が見分けられるかを検証し、
 * 割ったら exit 1 で落ちる。
 *
 * パレット間の距離は報告のみで失敗条件にしていない。「普通の色」は最大彩度から2割しか
 * 離れておらず（Material 500 の cusp 彩度比が平均 0.80）、solid と neon を完全に
 * 分離することは原理的にできないため。neon の色相を規則で選んでいる限り 0.06 前後は保たれる。
 */

import { SubscriptionPlan } from '../utils/subscription';
import { parseCustomAccent } from './accentCustom';

export type AccentPaletteKey = 'solid' | 'neon' | 'pastel' | 'custom';

export interface AccentPalette {
  key: AccentPaletteKey;
  label: string;
  description: string;
  /**
   * 選択に必要な subscription_plan の下限。プリセットは有料なら全部使える方針なので PRO(=1) 止まり。
   * ここは表示制御にすぎない。**サーバー側でも同じ検証が必要**で、
   * それが無い限り課金機能としては成立しない（PATCH に任意の値を投げられるため）。
   */
  minPlan: number;
  /**
   * UI に出さない。**定義は消さないこと。**
   * 既に選んでいるユーザーの色を巻き上げないための状態で、
   * resolveAccent は今までどおり通す（選び直しはできないが、今の見た目は保たれる）。
   */
  hidden?: boolean;
}

/**
 * カスタム色を表す key。`personalize.d.ac` にこの値が入っているとき、
 * 実際の色は `personalize.d.acx` の hex から取る。
 */
export const CUSTOM_ACCENT_KEY = 'custom';

// 並び順はそのまま UI の並び順。solid を先頭に置いているのは、これが基本色セットで
// 大半の利用者が選ぶ場所だから。neon はブランドの色だが色数が少なく用途が尖っている。
//
// 【4つ目のパレットを足そうとしないこと】
// 地が #121212 である以上、**暗い方向の設計空間は存在しない**。ジュエル系（濃い高彩度）の
// 代表色を実測すると #6a1b9a=1.99、#8e24aa=2.66、#c2185b=3.19 で、下限どころか 3:1 も割る。
// 反対側の「くすみ系」（低彩度）も試したが、彩度 0.30 では**4色でも互いに ΔE 0.087 で
// 見分けがつかず**成立しなかった。使える自由軸は彩度だけで、そこに入る帯は3本が上限。
// 4つ目が要るなら彩度・明度とは別の軸（単色hex以外の保存形式）が必要になる。
//
// 【カスタムだけは「4つ目のパレット」ではない】
// 上の注意書きは**プリセットの束を増やすな**という話で、カスタムは色を並べない。
// 選べる座標系そのものを安全域に限定しているので、彩度の帯が3本という制約とは別軸。
// 実装は constants/accentCustom.ts。
export const ACCENT_PALETTES: AccentPalette[] = [
  { key: 'solid', label: 'Solid', description: '基本の12色', minPlan: 0 },
  { key: 'neon', label: 'PULSE Neon', description: '蛍光色', minPlan: 0 },
  // 取り下げ済み。**定義は残すこと**（既に選んでいる人の色を巻き上げないため）
  { key: 'pastel', label: 'Pastel', description: '淡く柔らかい', minPlan: SubscriptionPlan.PRO, hidden: true },
  { key: 'custom', label: 'Custom', description: '自由に作る', minPlan: SubscriptionPlan.PRO },
];

/** UI に並べるパレット。取り下げたものは出さない */
export const VISIBLE_ACCENT_PALETTES = ACCENT_PALETTES.filter(p => !p.hidden);

export interface AccentPreset {
  key: string;
  palette: AccentPaletteKey;
  hex: string;
  label: string;
  /** #121212 に対するコントラスト比。生成スクリプトの出力（手で書かない） */
  contrast: number;
  /** OKLCH の色相角。参考値。並び順の確認と色相の穴を見るのに使う */
  hue: number;
}

// 既定は neon-lime。theme.ts の palette.primary.main と同じ値なので、
// 未設定のユーザーの見た目は今までと変わらない。ここを変えると全員に影響する。
// この1色だけは生成値(#d9fe00)ではなく theme.ts に合わせて #d9ff00 に固定してある。
export const DEFAULT_ACCENT = 'neon-lime';

// ここから scripts/genAccentPresets.js の出力。**手で編集しないこと。**
export const ACCENT_PRESETS: AccentPreset[] = [
  // solid — 12色 / Tailwind 400と500の中点(450)
  { key: 'solid-lime',         palette: 'solid',      hex: '#93d927', label: 'ライム',            contrast: 10.88,  hue: 130 },
  { key: 'solid-green',        palette: 'solid',      hex: '#38d16f', label: 'グリーン',          contrast: 9.38,   hue: 151 },
  { key: 'solid-teal',         palette: 'solid',      hex: '#22c6b2', label: 'ティール',          contrast: 8.73,   hue: 182 },
  { key: 'solid-cyan',         palette: 'solid',      hex: '#16c4e1', label: 'シアン',            contrast: 8.94,   hue: 214 },
  { key: 'solid-blue',         palette: 'solid',      hex: '#4d94f8', label: 'ブルー',            contrast: 6.16,   hue: 257 },
  { key: 'solid-indigo',       palette: 'solid',      hex: '#717af5', label: 'インディゴ',        contrast: 5.18,   hue: 277 },
  { key: 'solid-purple',       palette: 'solid',      hex: '#b46efa', label: 'パープル',          contrast: 5.84,   hue: 305 },
  { key: 'solid-fuchsia',      palette: 'solid',      hex: '#e162f4', label: 'フクシア',          contrast: 6.47,   hue: 322 },
  { key: 'solid-pink',         palette: 'solid',      hex: '#f05ea7', label: 'ピンク',            contrast: 6.12,   hue: 352 },
  { key: 'solid-red',          palette: 'solid',      hex: '#f45c5b', label: 'レッド',            contrast: 5.82,   hue: 24 },
  { key: 'solid-orange',       palette: 'solid',      hex: '#fa832c', label: 'オレンジ',          contrast: 7.45,   hue: 52 },
  { key: 'solid-amber',        palette: 'solid',      hex: '#f8af19', label: 'アンバー',          contrast: 9.94,   hue: 78 },
  // neon — 8色 / 最大彩度（生成）＋手選択2色
  { key: 'neon-lime',          palette: 'neon',       hex: '#d9ff00', label: 'ライム',            contrast: 16.28,  hue: 120 },
  { key: 'neon-green',         palette: 'neon',       hex: '#23ff00', label: 'グリーン',          contrast: 13.72,  hue: 142 },
  { key: 'neon-spring',        palette: 'neon',       hex: '#00fe8f', label: 'スプリンググリーン',  contrast: 13.89,  hue: 154 },
  { key: 'neon-aqua',          palette: 'neon',       hex: '#00fcfe', label: 'アクア',            contrast: 14.59,  hue: 196 },
  { key: 'neon-blue',          palette: 'neon',       hex: '#0051ff', label: 'ブルー',            contrast: 3.23,   hue: 263 },
  { key: 'neon-violet',        palette: 'neon',       hex: '#d200ff', label: 'バイオレット',      contrast: 4.62,   hue: 318 },
  { key: 'neon-magenta',       palette: 'neon',       hex: '#ff00d5', label: 'マゼンタ',          contrast: 5.54,   hue: 338 },
  { key: 'neon-red',           palette: 'neon',       hex: '#ff2600', label: 'レッド',            contrast: 4.93,   hue: 31 },
  // pastel — 8色 / Tailwind 300
  { key: 'pastel-lime',        palette: 'pastel',     hex: '#bef264', label: 'ライム',            contrast: 14.34,  hue: 127 },
  { key: 'pastel-green',       palette: 'pastel',     hex: '#86efac', label: 'グリーン',          contrast: 13.34,  hue: 154 },
  { key: 'pastel-cyan',        palette: 'pastel',     hex: '#67e8f9', label: 'シアン',            contrast: 12.92,  hue: 207 },
  { key: 'pastel-blue',        palette: 'pastel',     hex: '#93c5fd', label: 'ブルー',            contrast: 10.39,  hue: 252 },
  { key: 'pastel-purple',      palette: 'pastel',     hex: '#d8b4fe', label: 'パープル',          contrast: 10.60,  hue: 306 },
  { key: 'pastel-red',         palette: 'pastel',     hex: '#fca5a5', label: 'レッド',            contrast: 9.87,   hue: 20 },
  { key: 'pastel-orange',      palette: 'pastel',     hex: '#fdba74', label: 'オレンジ',          contrast: 11.11,  hue: 66 },
  { key: 'pastel-yellow',      palette: 'pastel',     hex: '#fde047', label: 'イエロー',          contrast: 14.21,  hue: 98 },
];

const DEFAULT_ACCENT_HEX = ACCENT_PRESETS.find(p => p.key === DEFAULT_ACCENT)!.hex;

/**
 * 実際に描画する色。
 *
 * @param customHex `personalize.d.acx`。**生の保存値を渡してよい**（中で検証する）。
 *   カスタム以外のときは無視される。
 */
export const accentHex = (key: string | undefined, customHex?: unknown): string => {
  if (key === CUSTOM_ACCENT_KEY) return parseCustomAccent(customHex) ?? DEFAULT_ACCENT_HEX;
  return ACCENT_PRESETS.find(p => p.key === key)?.hex ?? DEFAULT_ACCENT_HEX;
};

/** プランで選べるかどうか */
export const canUsePalette = (palette: AccentPaletteKey, plan: number): boolean =>
  plan >= (ACCENT_PALETTES.find(p => p.key === palette)?.minPlan ?? 0);

/**
 * 保存値を「実際に描画してよい key」に落とす。**描画前に必ず通すこと。**
 *
 * 有料の装飾は解約したら使えなくなる（そうしないと1ヶ月だけ課金して永久に使える）。
 * ただし保存値そのものは消さない。再契約すれば元の見た目が自動で戻る。
 * 判定に使うのは**プロフィール所有者の**プランであって、閲覧者のプランではない。
 */
export const resolveAccent = (key: string | undefined, plan: number): string => {
  // カスタムはプリセット表に無い。プランだけで判定する
  // （色そのものの妥当性は accentHex 側の parseCustomAccent が見る）
  if (key === CUSTOM_ACCENT_KEY) {
    return canUsePalette('custom', plan) ? CUSTOM_ACCENT_KEY : DEFAULT_ACCENT;
  }
  const preset = ACCENT_PRESETS.find(p => p.key === key);
  return preset && canUsePalette(preset.palette, plan) ? preset.key : DEFAULT_ACCENT;
};

/**
 * アクセントをどこまで広げるか。
 *
 * 「SNSアイコンだけ色を付ける」のような**要素ごとのスイッチを作らないこと**。
 * 色を乗せられる場所は際限なく増えるので、そのたびに設定が1つ増えて破綻する。
 * 段階を1本の軸にまとめ、新しい要素はどの段階に属するかだけ決める。
 *
 * 守るべき制約が2つある。
 * - **段階は必ず包含関係にすること。** 上の段階は下の段階の要素を全部含む。
 *   「強めにすると見出しの色が消える」のような入れ替わりが起きると、
 *   結局ユーザーは混ぜたくなり、要素ごとのスイッチに逆戻りする。
 * - **本文には色を乗せない。** アクセントは暗い色も選べるので、
 *   文章に適用すると可読性がユーザーの選択次第で壊れる。対象はアイコンと罫線、数値まで。
 *
 * 【段階が2つから3つになった経緯】
 * 統計の数値は最初 'b' に入れたが、実機で見ると**数値は面積が小さいわりに視線を最も引く**ので、
 * 紹介文の面と同時に色が乗ると一段飛ばしたように見えた。数値だけを最上段へ切り出してある。
 *
 * **数値を2つの段階に割らないこと。** 「大きい数値は中段・小さい数値は上段」のような分け方は、
 * 軸を『どの要素か』から『同じ要素をどれだけか』へすり替える。境界に理屈が立たず、
 * 新しい数値を足すたびにどちらへ入れるかの判断が要るようになる。
 * 数値は1要素として扱い、まとめて最上段に置く。
 */
export type AccentLevelKey = 'n' | 'b' | 'f';

export interface AccentLevel {
  key: AccentLevelKey;
  /**
   * UI ではドットのメーターで並べるので画面に文字としては出ない。
   * **それでも消さないこと。** 選択中の段階の説明文と読み上げ・ツールチップに使う
   * （記号だけだと何が起きるか読めないため。ProfileCustomizePanel の CustomizeRow 参照）。
   */
  label: string;
  description: string;
  /** 大小比較用。包含関係を壊さないため、判定は必ずこの順位で行う */
  rank: number;
  minPlan: number;
}

// **並び順がそのままメーターの目盛りになる**（rank ではなく配列の位置で描く）ので、
// 途中に段階を挿し込むときは rank と並び順の両方を合わせること。
//
// 上2つを同じ PRO にしてあるのは意図的。装飾そのものでMAX限定を作らない方針で、
// 有料なら全部使える（STANDARD加入者が二級に感じないようにするため）。
export const ACCENT_LEVELS: AccentLevel[] = [
  { key: 'n', label: '標準', description: '通常のスタイル', rank: 0, minPlan: 0 },
  { key: 'b', label: '強め', description: '紹介文やSNSアイコンにも適用', rank: 1, minPlan: SubscriptionPlan.PRO },
  { key: 'f', label: '全域', description: '統計の数値にも適用', rank: 2, minPlan: SubscriptionPlan.PRO },
];

export const DEFAULT_ACCENT_LEVEL: AccentLevelKey = 'n';

export const canUseAccentLevel = (key: string, plan: number): boolean =>
  plan >= (ACCENT_LEVELS.find(l => l.key === key)?.minPlan ?? 0);

export const resolveAccentLevel = (key: string | undefined, plan: number): AccentLevelKey => {
  const level = ACCENT_LEVELS.find(l => l.key === key);
  return level && plan >= level.minPlan ? level.key : DEFAULT_ACCENT_LEVEL;
};

/** その段階に達しているか。要素側はこれだけを見る */
export const accentReaches = (current: AccentLevelKey, needed: AccentLevelKey): boolean =>
  (ACCENT_LEVELS.find(l => l.key === current)?.rank ?? 0)
  >= (ACCENT_LEVELS.find(l => l.key === needed)?.rank ?? 0);

/**
 * カードのスコアバッジの星をアクセント色にするか。
 *
 * **上の段階('al')には入れない。独立した ON/OFF。**
 * 段階の軸はプロフィールページそのものの装飾で、こちらが変えるのは作品カードの
 * バッジ（ライブラリや検索にも出る物）。段階の包含関係に混ぜると
 * 「強め」にしただけでカードの見え方まで動くことになる。
 *
 * **これを前例に要素ごとのスイッチを増やさないこと**（増やすなという話は上の段階の項）。
 */
export type CardScoreAccentKey = 'n' | 'a';

export const CARD_SCORE_ACCENTS: Array<{
  key: CardScoreAccentKey;
  label: string;
  description: string;
  minPlan: number;
}> = [
  { key: 'n', label: '標準', description: '金色のまま', minPlan: 0 },
  { key: 'a', label: 'アクセント', description: 'スコアの星をアクセント色にする', minPlan: SubscriptionPlan.PRO },
];

export const DEFAULT_CARD_SCORE_ACCENT: CardScoreAccentKey = 'n';

export const canUseCardScoreAccent = (key: string, plan: number): boolean =>
  plan >= (CARD_SCORE_ACCENTS.find(o => o.key === key)?.minPlan ?? 0);

export const resolveCardScoreAccent = (
  key: string | undefined,
  plan: number
): CardScoreAccentKey => {
  const option = CARD_SCORE_ACCENTS.find(o => o.key === key);
  return option && plan >= option.minPlan ? option.key : DEFAULT_CARD_SCORE_ACCENT;
};
